import { ButtonIcon } from '@data-products-and-ai/react-components';
import React, { useEffect, useState } from 'react';
import axiosConfig from '../../utils/axiosConfig';
import { getProjects, ProjectsType } from '../../utils/ProjectUtils';
import useToken from '../useToken';

export interface ListItem {
  id: string;
  name: string;
}

interface TemplateListProps {
  onSelectProject: (project: string) => void;
  selectedProject: string;
}

const TemplateList: React.FC<TemplateListProps> = ({
  onSelectProject,
  selectedProject,
}) => {
  const [projects, setProjects] = useState<ProjectsType[]>([]);
  const [templates, setTemplates] = useState<ListItem[]>([]);
  const getToken = useToken();

  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      const fetchedProjects = getProjects(token.groupIds);
      if (fetchedProjects.length === 0) {
        setProjects([{ key: 'no-projects', name: 'No projects available' }]);
        return;
      }
      setProjects(fetchedProjects);
      onSelectProject(fetchedProjects[0].key);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      axiosConfig
        .post(
          '/list_templates',
          {
            group: selectedProject,
          },
          {
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        )
        .then((response) => {
          const fetchedTemplates = response.data.map((template: string) => {
            return { id: template, name: template.split('Templates/')[1] };
          });
          setTemplates(fetchedTemplates);
        })
        .catch((error) => {
          console.log(error);
        });
    };
    if (selectedProject) {
      fetchData();
    }
  }, [getToken, selectedProject]);

  const onChangeProject = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onSelectProject(event.target.value);
  };

  const handleDownloadTemplate = async (templateId: string) => {
    const token = await getToken();
    axiosConfig
      .post(
        '/download_file',
        {
          group: selectedProject,
          template_name: templateId,
        },
        {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
          },
        },
      )
      .then((resp) => {
        const url = resp.data.download_url;
        if (url) {
          window.open(url, '_blank');
        } else {
          console.log('No download url available');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleDownloadAllTemplates = async () => {
    const token = await getToken();
    axiosConfig
      .post(
        '/download_all_files',
        {
          template_name: 'Templates/All_templates.zip',
          group: selectedProject,
        },
        {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
          },
        },
      )
      .then((resp) => {
        const url = resp.data.download_all_files_url;
        if (url) {
          window.open(url, '_blank');
        } else {
          console.log('No download url available');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div className="templateList">
      <select className="projectSelector" onChange={onChangeProject}>
        {projects.map((project) => (
          <option
            key={project.key}
            className="projectSelector_option"
            data-testid="project-item"
            value={project.key}
          >
            {project.name}
          </option>
        ))}
      </select>
      <div className="templateList-container">
        <div className="templateList-header">
          <h4 className="templateList-header_title">Templates</h4>
          <ButtonIcon
            icon="IconDownload"
            disabled={templates.length === 0}
            onClick={() => handleDownloadAllTemplates()}
            hoverType="color"
            size="medium"
          />
        </div>
        <ul className="templateList-list">
          {templates.length ? (
            templates.map((template) => (
              <li
                key={template.id}
                className="templateList-list_item"
                data-testid="template-item"
              >
                {template.name}
                <ButtonIcon
                  icon="IconDownload"
                  hoverType="color"
                  size="small"
                  onClick={() => handleDownloadTemplate(template.id)}
                />
              </li>
            ))
          ) : (
            <div className="templateList-list">
              <p className="templateList-list_item">No templates available</p>
            </div>
          )}
        </ul>
      </div>
    </div>
  );
};

export default TemplateList;
